import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const websitePackages = [
  {
    name: 'Launch page',
    price: 'CA$695',
    note: 'For one focused offer',
    items: ['One responsive page', 'Proven page structure, tailored to your brand', 'Contact or booking link', 'One revision round', 'Basic metadata and launch support'],
  },
  {
    name: 'Custom one-page site',
    price: 'CA$1,250',
    note: 'For a stronger first impression',
    items: ['Original visual direction', 'Conversion-focused page content', 'Custom responsive build', 'Analytics and Search Console setup', 'Two revision rounds'],
  },
  {
    name: 'Business website',
    price: 'CA$1,995+',
    note: 'For a business ready to be found',
    items: ['Up to five core pages', 'Service and trust-page structure', 'Local SEO foundation', 'Domain / hosting handoff', 'Launch and post-launch checks'],
  },
];

const investments = [
  ['Custom software', 'Discovery from CA$750; implementation is scoped after the workflow, data, integrations, and support needs are understood.'],
  ['AI automation', 'A focused pilot starts at CA$1,500. Usage fees for models and workflow platforms stay visible and separate.'],
  ['Website care', 'Maintenance begins at CA$149/month for checks, routine updates, and a small support allowance.'],
];

const Pricing = () => (
  <>
    <Helmet>
      <title>Website Pricing Toronto | Nexgeius</title>
      <meta name="description" content="Straightforward website pricing for Toronto small businesses: launch pages from CA$695, custom one-page sites from CA$1,250, and business websites from CA$1,995." />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href="https://www.nexgeius.com/pricing" />
    </Helmet>
    <main>
      <section className="bg-[#102A3A] px-6 pb-20 pt-32 text-white lg:px-8 lg:pb-24">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1.15fr_.85fr] lg:items-end">
          <div>
            <p className="text-xs font-bold uppercase tracking-[.2em] text-[#92C5D8]">Straightforward investment</p>
            <h1 className="mt-5 max-w-4xl text-[clamp(3rem,6.2vw,6rem)] font-semibold leading-[.94] tracking-[-.06em]">Start small. Know what you are buying.</h1>
          </div>
          <p className="max-w-xl text-lg leading-8 text-[#C5D3DA] lg:justify-self-end">The entry package is intentionally narrow: a credible launch page for a clear offer. Custom strategy, more pages, integrations, and ongoing growth have their own scope so there are no surprise promises.</p>
        </div>
      </section>

      <section className="bg-[#F1F4F4] px-6 py-20 lg:px-8 lg:py-28">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-4 border-b border-[#C9D7DD] pb-8 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="section-kicker">Website development</p>
              <h2 className="mt-3 text-4xl font-semibold tracking-[-.045em] text-[#102A3A] md:text-5xl">Pick the right starting point.</h2>
            </div>
            <p className="max-w-md text-sm leading-6 text-[#63747E]">All prices are in Canadian dollars, before tax and third-party costs. A domain, hosting, email, booking software, and paid tools are billed by their providers.</p>
          </div>
          <div className="grid border-l border-[#C9D7DD] md:grid-cols-3">
            {websitePackages.map((pkg, index) => (
              <article key={pkg.name} className="border-b border-r border-[#C9D7DD] bg-[#F1F4F4] p-7 md:p-8">
                <p className="text-xs font-bold text-[#176B8A]">0{index + 1}</p>
                <h3 className="mt-8 text-2xl font-semibold tracking-[-.03em] text-[#102A3A]">{pkg.name}</h3>
                <p className="mt-2 text-sm text-[#63747E]">{pkg.note}</p>
                <p className="mt-7 text-3xl font-semibold tracking-[-.04em] text-[#102A3A]">{pkg.price}</p>
                <ul className="mt-8 space-y-3 border-t border-[#C9D7DD] pt-6 text-sm leading-6 text-[#52636E]">
                  {pkg.items.map((item) => <li key={item} className="flex gap-3"><span className="text-[#176B8A]" aria-hidden="true">+</span>{item}</li>)}
                </ul>
              </article>
            ))}
          </div>
          <p className="mt-5 max-w-3xl text-sm leading-6 text-[#63747E]">The CA$695 launch page is not a custom software project and does not include e-commerce, a CMS, paid photography, custom integrations, ongoing SEO, or unlimited revisions. If you need those, we will quote the work clearly before it begins.</p>
        </div>
      </section>

      <section className="bg-white px-6 py-20 lg:px-8 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[.78fr_1.22fr]">
          <div>
            <p className="section-kicker">Systems and support</p>
            <h2 className="mt-4 text-4xl font-semibold leading-[1.04] tracking-[-.045em] text-[#102A3A] md:text-5xl">Price comes after the problem is clear.</h2>
          </div>
          <dl className="border-t border-[#C9D7DD]">
            {investments.map(([term, description]) => (
              <div key={term} className="grid gap-3 border-b border-[#C9D7DD] py-6 sm:grid-cols-[190px_1fr]">
                <dt className="font-semibold text-[#102A3A]">{term}</dt>
                <dd className="text-sm leading-6 text-[#63747E]">{description}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="bg-[#DDE9E7] px-6 py-20 lg:px-8 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1.15fr_.85fr] lg:items-end">
          <div>
            <p className="section-kicker">A useful first conversation</p>
            <h2 className="mt-4 max-w-4xl text-[clamp(2.6rem,5vw,4.8rem)] font-semibold leading-[.98] tracking-[-.052em] text-[#102A3A]">Tell us the goal and the budget range. We will recommend the smallest package that can do the job.</h2>
          </div>
          <Link to="/contact" className="button-primary inline-flex w-fit items-center gap-2 lg:justify-self-end">Start a project <span aria-hidden="true">&rarr;</span></Link>
        </div>
      </section>
    </main>
  </>
);

export default Pricing;
